import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import AdminLayout from "../components/AdminLayout";
import { TrialBanner } from "../components/TrialBanner";
import { 
  Trophy, Users, DollarSign, Building, 
  ClipboardList, ChevronRight, Calendar,
  CreditCard, CheckCircle, AlertCircle, Link2, ExternalLink,
  TrendingUp, Download, ArrowUpRight, Clock,
  Ticket, UserCheck, AlertTriangle, CalendarDays, Wallet,
  Shield, Plus, Send, FileText, Activity, Zap,
  UserPlus, CreditCard as CardIcon, ChevronDown
} from "lucide-react";
import axios from "axios";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from "recharts";
import { format, isAfter, isBefore, addDays, parseISO } from "date-fns";

const CHART_COLORS = ['#16b8a6', '#087c71', '#8da39c', '#d8a33b', '#cb5b56'];

const ACTIVITY_ICONS = {
  registration: ClipboardList,
  payment: DollarSign,
  coach_added: UserPlus,
  compliance_submitted: Shield,
};

const ACTIVITY_COLORS = {
  registration: "bg-blue-100 text-blue-600",
  payment: "bg-emerald-100 text-emerald-600",
  coach_added: "bg-violet-100 text-violet-600",
  compliance_submitted: "bg-amber-100 text-amber-600",
};

const DashboardContent = ({ children }) => <>{children}</>;
const identityPath = (path) => path;

// Embedded Today uses the incumbent markup without mounting a second admin shell.
const AdminDashboard = ({ embedded = false, organization: suppliedOrganization, mapPath = identityPath } = {}) => {
  const { user, token } = useAuth();
  const routerNavigate = useNavigate();
  const navigate = (path) => routerNavigate(mapPath(path));
  const Layout = embedded ? DashboardContent : AdminLayout;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [stats, setStats] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [extras, setExtras] = useState(null);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [chartDays, setChartDays] = useState(30);
  const canAccess = useCallback((permission) => (
    user?.role !== "org_admin"
    || !Array.isArray(user?.admin_permissions)
    || user.admin_permissions.includes(permission)
  ), [user?.role, user?.admin_permissions]);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchPrograms = useCallback(async (headers) => {
    if (!canAccess("programs")) return { data: [] };
    if (!embedded) return axios.get(`${API}/programs`, { headers }).catch(() => ({ data: [] }));

    // Counts and closing-soon tasks include every page, not just the preview.
    const items = [];
    let page = 1;
    while (true) {
      const { data } = await axios.get(`${API}/programs`, { headers, params: { page, page_size: 100 } });
      if (Array.isArray(data)) return { data };
      if (!Array.isArray(data?.items) || !Number.isInteger(data.total) || data.total < 0) {
        throw new Error("Invalid programs response");
      }
      items.push(...data.items);
      if (items.length >= data.total) return { data: items };
      if (!data.items.length || data.has_next === false) throw new Error("Incomplete programs response");
      page += 1;
    }
  }, [canAccess, embedded]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const headers = getHeaders();
      const [statsRes, analyticsRes, extrasRes, registrationsRes, orgRes, programsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers, params: { days: chartDays } }),
        axios.get(`${API}/admin/dashboard-extras`, { headers }),
        canAccess("registrations")
          ? axios.get(`${API}/registrations`, { headers, params: { page: 1, page_size: 5 } })
          : Promise.resolve({ data: { items: [] } }),
        suppliedOrganization
          ? Promise.resolve({ data: suppliedOrganization })
          : axios.get(`${API}/orgs/me`, { headers }).catch(() => ({ data: null })),
        fetchPrograms(headers)
      ]);

      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setExtras(extrasRes.data);
      setOrganization(orgRes.data);
      setPrograms(Array.isArray(programsRes.data) ? programsRes.data : []);
      const regData = registrationsRes.data.items || registrationsRes.data;
      setRecentRegistrations(Array.isArray(regData) ? regData.slice(0, 5) : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [chartDays, getHeaders, canAccess, fetchPrograms, suppliedOrganization]);

  // Refetch analytics when chart days change
  const refetchAnalytics = useCallback(async () => {
    try {
      const headers = getHeaders();
      const res = await axios.get(`${API}/admin/analytics`, { headers, params: { days: chartDays } });
      setAnalytics(res.data);
    } catch (err) {
      console.error("Error refetching analytics:", err);
      if (embedded) setLoadError(true);
    }
  }, [chartDays, getHeaders, embedded]);

  useEffect(() => {
    fetchData();
  }, [user?.org_id, token, suppliedOrganization?.org_id, canAccess]);

  useEffect(() => {
    if (!loading) refetchAnalytics();
  }, [chartDays]);

  // Derived data
  const now = new Date();
  const activePrograms = programs.filter(p => {
    const regClose = p.registration_close_date ? parseISO(p.registration_close_date) : null;
    const startDate = p.start_date ? parseISO(p.start_date) : null;
    return (regClose && isAfter(regClose, now)) || (startDate && isAfter(startDate, now));
  }).slice(0, 4);

  const activeProgramTotal = programs.filter((program) => program.status === "active" || !program.status).length;

  const pendingPayments = analytics?.payment_breakdown?.pending || 0;

  const closingSoonPrograms = programs.filter(p => {
    if (!p.registration_close_date) return false;
    const closeDate = parseISO(p.registration_close_date);
    return isAfter(closeDate, now) && isBefore(closeDate, addDays(now, 7));
  });

  // Roster fill for active programs
  const rosterFill = extras?.roster_fill || [];
  const activeFill = rosterFill.filter(r => {
    const regClose = r.registration_close_date ? parseISO(r.registration_close_date) : null;
    const startDate = r.start_date ? parseISO(r.start_date) : null;
    return (regClose && isAfter(regClose, now)) || (startDate && isAfter(startDate, now));
  }).slice(0, 4);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-5" role="status" aria-busy="true" aria-label="Loading dashboard">
          <div className="h-5 w-40 rounded-full bg-slate-200 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((item) => <div key={item} className="h-36 platform-card bg-white/70 animate-pulse" />)}
          </div>
          <div className="h-80 platform-card bg-white/70 animate-pulse" />
        </div>
      </Layout>
    );
  }

  if (loadError) {
    return (
      <Layout title="Today" subtitle="The work that needs your attention across the organization.">
        <div className="platform-card p-10 text-center max-w-2xl mx-auto" role="alert">
          <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-5 h-5" />
          </div>
          <h2 className="text-xl text-[#101a19]">We couldn’t load your command center</h2>
          <p className="text-slate-500 mt-2 mb-5">We couldn’t load current information. Check your connection and try again.</p>
          <Button className="platform-action platform-action-primary" onClick={fetchData}>Try again</Button>
        </div>
      </Layout>
    );
  }

  const registrationWeek = analytics?.registration_trends?.slice(-7).reduce((sum, day) => sum + day.registrations, 0) || 0;
  const attentionItems = [
    {
      label: "Pending registrations",
      detail: "Require review or approval",
      value: (embedded ? stats.pending_count : stats.pending_registrations) || 0,
      action: "Review",
      path: "/admin/registrations",
      permission: "registrations",
    },
    {
      label: "Payments awaiting action",
      detail: "Open or incomplete payments",
      value: pendingPayments,
      action: "View",
      path: "/admin/financials",
      permission: "payments",
    },
    {
      label: "Programs closing soon",
      detail: "Registration closes within 7 days",
      value: closingSoonPrograms.length,
      action: "Review",
      path: "/admin/programs",
      permission: "programs",
    },
  ].filter((item) => canAccess(item.permission));

  const workflowSteps = [
    ["01", "Registration", "/admin/registrations", "registrations"],
    ["02", "Teams", "/admin/programs", "programs"],
    ["03", "Schedules", "/admin/scheduling", "teams"],
    ["04", "Communication", "/admin/messaging", "teams"],
  ].filter(([, , , permission]) => canAccess(permission));


  const hero = (<>
        <section className="overview-hero" aria-labelledby="overview-heading">
          <div>
            <p className="platform-eyebrow">Organization overview</p>
            <h1 id="overview-heading">{embedded ? "Today" : <>Everything,<br />connected.</>}</h1>
          </div>
          {canAccess("programs") && (
            <Button className="platform-action platform-action-teal" onClick={() => navigate("/admin/programs/new")}>
              Create program <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </section>

 </>);
  const workflow = (<>
        {workflowSteps.length > 0 && <nav className="workflow-rail" aria-label="Available Sporthouse workflow">
          {workflowSteps.map(([number, label, path], index) => (
            <button key={label} onClick={() => navigate(path)} className="workflow-step">
              <span><small>{number}</small>{label}</span>
              {index < workflowSteps.length - 1 && <ChevronRight aria-hidden="true" />}
            </button>
          ))}
        </nav>}


</>);
  const attention = (<>
          <section className="platform-card overview-module attention-module" aria-labelledby="needs-attention-title">
            <div className="module-heading">
              <h2 id="needs-attention-title" className="platform-eyebrow">Needs attention</h2>
              {attentionItems.length > 0 && <button onClick={() => navigate(attentionItems[0].path)}>View all <ArrowUpRight /></button>}
            </div>
            <div>
              {attentionItems.length > 0 ? attentionItems.map((item, index) => (
                <button key={item.label} onClick={() => navigate(item.path)} className="attention-row">
                  <span className="attention-index">{String(index + 1).padStart(2, "0")}</span>
                  <span className="attention-copy"><strong>{item.label}</strong><small>{item.detail}</small></span>
                  <strong className="attention-value">{item.value}</strong>
                  <span className="attention-action">{item.action} <ArrowUpRight /></span>
                </button>
              )) : <div className="module-empty"><CheckCircle /><strong>No restricted tasks</strong><span>Your access is focused on organization insights.</span></div>}
            </div>
          </section>
</>);
  const intelligence = (<>
          <section className="intelligence-module" aria-labelledby="intelligence-title">
            <h2 id="intelligence-title" className="platform-eyebrow">Intelligence</h2>
            <div className="intelligence-copy">
              <Zap aria-hidden="true" />
              <p><strong>{registrationWeek} registrations this week.</strong><br />{embedded
                ? (canAccess("programs") ? <>Your organization has {activeProgramTotal} active program{activeProgramTotal === 1 ? "" : "s"}.</> : "Program information is outside your access.")
                : <>Your organization has {activePrograms.length} active program{activePrograms.length === 1 ? "" : "s"} accepting registrations.</>}</p>
            </div>
            <button onClick={() => navigate("/admin/reports")}>View full insights <ArrowUpRight /></button>
          </section>
</>);
  const upcoming = (<>
          <section className="platform-card overview-module today-module" aria-labelledby="today-title">
            <div className="module-heading">
              <h2 id="today-title" className="platform-eyebrow">Upcoming activity</h2>
              {canAccess("teams") && <button onClick={() => navigate("/admin/scheduling")}>View schedule <ArrowUpRight /></button>}
            </div>
            {(extras?.upcoming_events || []).length > 0 ? (
              <div className="event-list">
                {extras.upcoming_events.slice(0, 5).map((event, index) => (
                  <div className="event-row" key={`${event.event_id || event.game_id || "event"}-${index}`}>
                    <time>{event.start_time ? format(new Date(event.start_time), "MMM d · h:mm a") : "Upcoming"}</time>
                    <span><strong>{event.title || event.name || event.event_type || "Scheduled activity"}</strong><small>{event.program || event.location || "Organization schedule"}</small></span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="module-empty"><CalendarDays /><strong>No upcoming activity</strong><span>New events will appear here when they are scheduled.</span></div>
            )}
          </section>
</>);
  const finance = (<>
          <section className="platform-card overview-module finance-module" aria-labelledby="finance-title">
            <div className="module-heading">
              <h2 id="finance-title" className="platform-eyebrow">Financial summary</h2>
              {canAccess("payments") && <button onClick={() => navigate("/admin/financials")}>View report <ArrowUpRight /></button>}
            </div>
            <dl>
              <div><dt>Total collected</dt><dd>${(stats.revenue || 0).toLocaleString()}</dd></div>
              <div><dt>Outstanding balance</dt><dd>${(stats.pending_amount || 0).toLocaleString()}</dd></div>
              <div><dt>Collected this month</dt><dd>${(extras?.revenue_periods?.this_month || 0).toLocaleString()}</dd></div>
            </dl>
          </section>
</>);
  const operationalDetails = (<>
        {/* === #1: Coach & Staff Summary Card === */}
        {canAccess("registrations") && extras?.coach_summary && (
          <div className="platform-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-slate-900">Coaches & Staff</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/people")} className="rounded-xl text-slate-600">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{extras.coach_summary.total_coaches}</p>
                <p className="text-xs text-slate-500 mt-1">Coaches</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{extras.coach_summary.total_staff}</p>
                <p className="text-xs text-slate-500 mt-1">Staff</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-xl">
                <p className="text-2xl font-bold text-emerald-600">{extras.coach_summary.approved}</p>
                <p className="text-xs text-slate-500 mt-1">Approved</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-xl">
                <p className="text-2xl font-bold text-amber-600">{extras.coach_summary.pending_approval}</p>
                <p className="text-xs text-slate-500 mt-1">Pending</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-xl">
                <p className="text-2xl font-bold text-emerald-600">{extras.coach_summary.compliant}</p>
                <p className="text-xs text-slate-500 mt-1">Compliant</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-xl cursor-pointer hover:bg-red-100 transition-colors"
                   onClick={() => navigate("/admin/compliance")}>
                <p className="text-2xl font-bold text-red-600">{extras.coach_summary.needs_action}</p>
                <p className="text-xs text-slate-500 mt-1">Needs Action</p>
              </div>
            </div>
          </div>
        )}

        {/* === Main Content Grid === */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Registration Trends Chart with #5: Date Range Selector */}
          <div className="platform-card lg:col-span-2 p-6">
            <div className="athletic-chart-heading flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-slate-900">Registration Activity</h3>
                <p className="text-sm text-slate-500">Last {chartDays} days</p>
              </div>
              <div className="athletic-chart-controls flex items-center gap-3">
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                  {[7, 30, 90].map(d => (
                    <button
                      key={d}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        chartDays === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                      onClick={() => setChartDays(d)}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-emerald-600 font-medium">
                    +{analytics?.registration_trends?.slice(-7).reduce((a, b) => a + b.registrations, 0) || 0} this week
                  </span>
                </div>
              </div>
            </div>
            <div className="h-64">
              {analytics?.registration_trends && analytics.registration_trends.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics.registration_trends}>
                    <defs>
                      <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis 
                      dataKey="label" 
                      tick={{ fontSize: 12, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                      interval={chartDays <= 7 ? 0 : chartDays <= 30 ? 4 : 13}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="registrations" 
                      stroke="#6366f1" 
                      strokeWidth={2}
                      fill="url(#colorRegistrations)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No registration data yet</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column — #4: Balance Breakdown (replaces Stripe widget redundancy #12) + #2: Activity Feed */}
          <div className="space-y-6">
            {/* #4: Outstanding Balance Breakdown */}
            <div className="platform-card p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Outstanding Balances</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="text-slate-700">Paid</span>
                  </div>
                  <span className="font-semibold text-emerald-700">{analytics?.payment_breakdown?.paid || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-500" />
                    <div>
                      <span className="text-slate-700">Not Yet Paid</span>
                      <p className="text-xs text-slate-400">${(extras?.balance_breakdown?.unpaid_amount || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  <span className="font-semibold text-slate-600">{extras?.balance_breakdown?.unpaid || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-blue-500" />
                    <div>
                      <span className="text-slate-700">Plan — Current</span>
                      <p className="text-xs text-slate-400">${(extras?.balance_breakdown?.plan_current_amount || 0).toLocaleString()} remaining</p>
                    </div>
                  </div>
                  <span className="font-semibold text-blue-600">{extras?.balance_breakdown?.plan_current || 0}</span>
                </div>
                {(extras?.balance_breakdown?.plan_overdue || 0) > 0 && (
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <div>
                        <span className="text-slate-700 font-medium">Plan — Overdue</span>
                        <p className="text-xs text-red-400">${(extras?.balance_breakdown?.plan_overdue_amount || 0).toLocaleString()} past due</p>
                      </div>
                    </div>
                    <span className="font-semibold text-red-600">{extras?.balance_breakdown?.plan_overdue || 0}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Programs Needing Attention */}
            {(() => {
              const needsAttention = programs.filter(p => {
                const closeDate = p.registration_close_date ? parseISO(p.registration_close_date) : null;
                const closingSoon = closeDate && isAfter(closeDate, now) && isBefore(closeDate, addDays(now, 14));
                const fillInfo = rosterFill.find(r => r.program_id === p.program_id);
                const lowFill = fillInfo && fillInfo.fill_pct < 25 && closingSoon;
                return lowFill;
              });
              
              return needsAttention.length > 0 ? (
                <div className="platform-card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <h3 className="font-semibold text-slate-900">Needs Attention</h3>
                  </div>
                  <div className="space-y-3">
                    {needsAttention.map(p => {
                      const fillInfo = rosterFill.find(r => r.program_id === p.program_id);
                      return (
                        <div key={p.program_id} className="p-3 bg-amber-50 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors"
                             onClick={() => navigate(`/admin/programs/${p.program_id}`)}>
                          <p className="font-medium text-amber-900 text-sm truncate">{p.name}</p>
                          <p className="text-xs text-amber-700 mt-1">
                            {fillInfo ? `${fillInfo.fill_pct}% full` : 'Low enrollment'} • Closes {format(parseISO(p.registration_close_date), "MMM d")}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </div>

        {/* === Middle row: Activity Feed + Upcoming Schedule === */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* #2: Recent Activity Feed */}
          <div className="platform-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-slate-600" />
                <h3 className="font-semibold text-slate-900">Recent Activity</h3>
              </div>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {extras?.activities && extras.activities.length > 0 ? (
                extras.activities.map((act, idx) => {
                  const Icon = ACTIVITY_ICONS[act.type] || Zap;
                  const colorClass = ACTIVITY_COLORS[act.type] || "bg-slate-100 text-slate-600";
                  return (
                    <div key={idx} className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800 truncate">{act.message}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {act.timestamp ? format(new Date(act.timestamp), "MMM d, h:mm a") : ""}
                        </p>
                      </div>
                      {act.meta?.amount && act.type === "payment" && (
                        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0">
                          ${act.meta.amount.toLocaleString()}
                        </span>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No recent activity</p>
                </div>
              )}
            </div>
          </div>

          {/* #7: Upcoming Schedule Preview */}
          {canAccess("teams") && <div className="platform-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-600" />
                <h3 className="font-semibold text-slate-900">Upcoming Schedule</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/scheduling")} className="rounded-xl text-slate-600">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="space-y-3">
              {extras?.upcoming_events && extras.upcoming_events.length > 0 ? (
                extras.upcoming_events.map((ev, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      ev.type === "game" ? "bg-blue-100" : "bg-violet-100"
                    }`}>
                      {ev.type === "game" ? (
                        <Trophy className="w-5 h-5 text-blue-600" />
                      ) : (
                        <CalendarDays className="w-5 h-5 text-violet-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{ev.title}</p>
                      <p className="text-xs text-slate-500">
                        {ev.start_time ? format(new Date(ev.start_time), "EEE, MMM d · h:mm a") : "TBD"}
                        {ev.program ? ` · ${ev.program}` : ""}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      ev.type === "game" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"
                    }`}>
                      {ev.type === "game" ? "Game" : ev.event_type || "Event"}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No upcoming events or games</p>
                </div>
              )}
            </div>
          </div>}
        </div>

        {/* === #3: Active Programs with Roster Fill Rates === */}
        {canAccess("programs") && (activeFill.length > 0 || activePrograms.length > 0) && (
          <div className="platform-card p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-semibold text-slate-900">Active Programs</h3>
                <p className="text-sm text-slate-500">Currently accepting registrations</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/programs")}
                className="text-slate-600 hover:text-slate-900 rounded-2xl"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(activeFill.length > 0 ? activeFill : activePrograms.map(p => ({
                program_id: p.program_id, name: p.name, sport_type: p.sport_type,
                registered: 0, capacity: p.max_participants || p.max_capacity || 0, fill_pct: 0,
                start_date: p.start_date, registration_close_date: p.registration_close_date,
              }))).map((program) => {
                const fillColor = program.fill_pct >= 90 ? "bg-red-500" :
                                  program.fill_pct >= 70 ? "bg-amber-500" :
                                  program.fill_pct >= 40 ? "bg-blue-500" : "bg-emerald-500";
                return (
                  <div 
                    key={program.program_id}
                    className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => navigate(`/admin/programs/${program.program_id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-violet-600" />
                      </div>
                      {program.sport_type && (
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {program.sport_type}
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-slate-900 mb-1 truncate">{program.name}</h4>
                    {program.start_date && (
                      <p className="text-xs text-slate-500 mb-3">
                        Starts {format(parseISO(program.start_date), "MMM d")}
                      </p>
                    )}
                    {/* Roster fill bar */}
                    {program.capacity > 0 ? (
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-500">{program.registered}/{program.capacity} spots</span>
                          <span className={`font-medium ${
                            program.fill_pct >= 90 ? "text-red-600" :
                            program.fill_pct >= 70 ? "text-amber-600" : "text-slate-600"
                          }`}>{program.fill_pct}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${fillColor}`}
                               style={{ width: `${Math.min(program.fill_pct, 100)}%` }} />
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400">No capacity set</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === Recent Registrations Table (#9: mobile-responsive) === */}
        {canAccess("registrations") && <div className="platform-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-semibold text-slate-900">Recent Registrations</h3>
              <p className="text-sm text-slate-500">Latest signups</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/registrations")}
              className="text-slate-600 hover:text-slate-900 rounded-2xl"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 text-slate-500 font-medium">Name</th>
                  <th className="text-left py-2 text-slate-500 font-medium">Program</th>
                  <th className="text-left py-2 text-slate-500 font-medium">Date</th>
                  <th className="text-right py-2 text-slate-500 font-medium">Amount</th>
                  <th className="text-right py-2 text-slate-500 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentRegistrations.map((reg, idx) => {
                  const name = reg.player_name || `${reg.first_name || ''} ${reg.last_name || ''}`.trim() || "—";
                  const progName = programs.find(p => p.program_id === reg.program_id)?.name || reg.program_name || "—";
                  return (
                    <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-3 text-slate-800">{name}</td>
                      <td className="py-3 text-slate-600 max-w-[200px] truncate">{progName}</td>
                      <td className="py-3 text-slate-500">
                        {reg.created_at ? format(new Date(reg.created_at), "MMM d") : "—"}
                      </td>
                      <td className="py-3 text-right text-slate-800 font-medium">
                        {reg.amount ? `$${reg.amount.toLocaleString()}` : "—"}
                      </td>
                      <td className="py-3 text-right">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          reg.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                          reg.payment_status === "partial" ? "bg-blue-100 text-blue-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {reg.payment_status || "pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* #9: Mobile cards */}
          <div className="md:hidden space-y-3">
            {recentRegistrations.map((reg, idx) => {
              const name = reg.player_name || `${reg.first_name || ''} ${reg.last_name || ''}`.trim() || "—";
              const progName = programs.find(p => p.program_id === reg.program_id)?.name || reg.program_name || "—";
              return (
                <div key={idx} className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-slate-800 text-sm">{name}</p>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      reg.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                      reg.payment_status === "partial" ? "bg-blue-100 text-blue-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>
                      {reg.payment_status || "pending"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{progName}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-slate-400">
                      {reg.created_at ? format(new Date(reg.created_at), "MMM d") : "—"}
                    </p>
                    <p className="text-sm font-medium text-slate-800">
                      {reg.amount ? `$${reg.amount.toLocaleString()}` : "—"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {recentRegistrations.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No registrations yet</p>
            </div>
          )}
        </div>}

        {/* Stripe Connect Status - consolidated (#12) */}
        {canAccess("settings") && canAccess("payments") && stats.stripe && !stats.stripe.connected && (
          <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-2xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-violet-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-violet-900">Connect Stripe to Accept Payments</h3>
                <p className="text-sm text-violet-700 mt-1">
                  Set up Stripe Connect to start collecting registration fees and manage payouts.
                </p>
                <Button
                  size="sm"
                  className="mt-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
                  onClick={() => navigate("/admin/settings")}
                >
                  <Link2 className="w-4 h-4 mr-1.5" /> Connect Stripe
                </Button>
              </div>
            </div>
          </div>
        )}

</>);
  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <TrialBanner organization={organization} />
        {hero}
        {embedded ? <>
          <section className="athletic-snapshot" aria-label="Organization snapshot">
            {canAccess("programs") && <div><span>Active programs</span><strong>{activeProgramTotal}</strong></div>}
            {canAccess("registrations") && <div><span>Awaiting review</span><strong>{stats.pending_count || 0}</strong></div>}
            <div><span>Registrations this week</span><strong>{registrationWeek}</strong></div>
            {canAccess("payments") && <div><span>Total collected</span><strong>${(stats.revenue || 0).toLocaleString()}</strong></div>}
          </section>
          <div className="athletic-priority-grid">{attention}{upcoming}</div>
          {canAccess("programs") && <>
        <section className="athletic-rosters platform-card" aria-labelledby="rosters-title">
          <div className="module-heading"><h2 id="rosters-title">Programs &amp; rosters</h2><button onClick={() => navigate("/admin/programs")}>All programs <ArrowUpRight /></button></div>
          {programs.length ? <ul>{programs.slice(0, 4).map(program => {
            const roster = rosterFill.find(item => item.program_id === program.program_id);
            const enrolled = roster?.registered ?? program.current_participants;
            const capacity = roster?.capacity ?? program.max_participants;
            return <li key={program.program_id}><button onClick={() => navigate(`/admin/programs/${program.program_id}`)}><strong>{program.name || "Untitled program"}</strong><span>{program.season || program.sport || "Season not set"}</span><span>{enrolled == null ? "Enrollment not provided" : `${enrolled}${capacity ? ` / ${capacity}` : ""} enrolled`}</span><span>{program.status || "Active"}</span><ArrowUpRight aria-hidden="true" /></button></li>;
          })}</ul> : <p className="p-5">No programs yet. Create a program to start building your season.</p>}
        </section></>}
          {workflow}
          <details className="athletic-operations"><summary><h2>Operational details</h2><span>Insights, finance, staff, activity and registrations</span></summary><div className="space-y-6"><div className="athletic-priority-grid">{intelligence}{finance}</div>{operationalDetails}</div></details>
        </> : <>{workflow}<div className="overview-grid">{attention}{intelligence}{upcoming}{finance}</div>{operationalDetails}</>}
      </div>
    </Layout>
  );
};

export default AdminDashboard;
